import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { useAuth } from '../../context/AuthContext';
import { useMask } from '../../context/MaskContext';
import { useTheme } from '../../context/ThemeContext';
import { notificationAPI, taskAPI, authAPI, otpFeedAPI } from '../../services/api';
import FloatingTools from '../FloatingTools'; // V70.148 — Hızlı Araçlar FAB (body'ye portal, tüm sayfalarda)
import WorkTypeSwitch from '../WorkTypeSwitch'; // V71.3.2 — çalışma şekli (Ofis / Remote)
import SantralCagriKarti from '../SantralCagriKarti'; // V71.36 — arayan ekranı (santral cebi çalarken)
import './TopBar.css';

/* ── Daily Motivation ── */
const MOTIVATION_MESSAGES = [
  // ── Orijinal 20 ──
  { icon: '✈️', text: 'İyi planlanmış bir seyahat, verimli geçen bir haftanın başlangıcıdır.' },
  { icon: '🎯', text: 'Bugünkü küçük adımlar, yarının büyük başarılarını oluşturur.' },
  { icon: '🌍', text: 'Her yeni rota, yeni bir fırsat demektir.' },
  { icon: '💡', text: 'Verimlilik, doğru araçları doğru zamanda kullanmaktır.' },
  { icon: '🤝', text: 'Güçlü iş ilişkileri, yüz yüze görüşmelerle kurulur.' },
  { icon: '📊', text: 'Veriye dayalı kararlar, her zaman daha iyi sonuç verir.' },
  { icon: '⭐', text: 'Mükemmellik bir alışkanlık, kaliteli hizmet bir seçimdir.' },
  { icon: '🚀', text: 'Süreçleri optimize etmek, zamandan ve enerjiden kazanmaktır.' },
  { icon: '🧭', text: 'Her seyahat planı, bir strateji fırsatıdır.' },
  { icon: '☕', text: 'Güzel bir gün, güzel bir planla başlar. İyi çalışmalar!' },
  { icon: '🏆', text: 'Başarılı ekipler, iyi organize edilmiş süreçlerle öne çıkar.' },
  { icon: '🔑', text: 'Detaylara özen göstermek, farkı yaratan şeydir.' },
  { icon: '💼', text: 'Profesyonellik, her adımda tutarlılık demektir.' },
  { icon: '🌟', text: 'Bugünkü işi bugünden daha iyi yapmanın yolunu bul.' },
  { icon: '📈', text: 'Sürekli iyileştirme, başarının anahtarıdır.' },
  { icon: '🎯', text: 'Hedefe odaklan, geri kalan her şey yerini bulur.' },
  { icon: '✨', text: 'Yeni bir gün, yeni fırsatlar. Enerjin yüksek olsun!' },
  { icon: '🛫', text: 'İyi bir seyahat deneyimi, mutlu çalışanlar demektir.' },
  { icon: '🌱', text: 'Küçük iyileştirmeler, büyük dönüşümlerin temelidir.' },
  { icon: '💪', text: 'Zorluklar, gelişim için birer fırsattır.' },
  // ── Seyahat & Turizm ──
  { icon: '🗺️', text: 'Her destinasyon bir hikaye, her rezervasyon bir başlangıçtır.' },
  { icon: '🌅', text: 'Müşterinin hayalini gerçeğe dönüştürmek, en güzel iş tanımıdır.' },
  { icon: '🧳', text: 'Bavul hazırlamak kolay, kusursuz bir plan hazırlamak sanattır.' },
  { icon: '🏖️', text: 'Tatil satmıyoruz, unutulmaz anılar tasarlıyoruz.' },
  { icon: '🛬', text: 'İyi bir karşılama, tüm seyahatin tonunu belirler.' },
  { icon: '🌏', text: 'Dünya küçüldükçe, doğru partnerin değeri büyüyor.' },
  { icon: '🎫', text: 'Bir bilet sadece kağıt değil, bir vaat ve güvendir.' },
  { icon: '🏨', text: 'Doğru otel seçimi, müşterinin sana olan güvenini pekiştirir.' },
  { icon: '🛳️', text: 'Denizaşırı fırsatlar, cesur planlarla yakalanır.' },
  { icon: '✈️', text: 'Uçuş saatleri değişebilir ama senin profesyonelliğin sabit kalmalı.' },
  // ── Müşteri & Hizmet ──
  { icon: '💎', text: 'Müşteri memnuniyeti tesadüf değil, özenli çalışmanın sonucudur.' },
  { icon: '📞', text: 'Hızlı dönüş yapmak, güvenin en kısa yoludur.' },
  { icon: '🎁', text: 'Beklentinin ötesine geçmek, sadık müşteri kazandırır.' },
  { icon: '🤗', text: 'Gülümseyerek başlayan bir iletişim, her kapıyı açar.' },
  { icon: '📝', text: 'İyi bir teklif, müşterinin ihtiyacını anladığını gösterir.' },
  { icon: '🔔', text: 'Zamanında hatırlatma yapmak, profesyonelliğin sessiz göstergesidir.' },
  { icon: '💬', text: 'Dinlemek, en güçlü satış aracıdır.' },
  { icon: '🌻', text: 'Her müşteri özeldir; şablonları aş, kişisel dokunuş kat.' },
  { icon: '🏅', text: 'Tekrar eden müşteri, en iyi referanstır.' },
  { icon: '🛎️', text: 'Hizmet kalitesi, en sessiz anlarında bile konuşur.' },
  // ── Ekip & İş Birliği ──
  { icon: '⚙️', text: 'İyi bir sistem, iyi insanların daha iyi çalışmasını sağlar.' },
  { icon: '🧩', text: 'Herkesin rolü önemlidir; ekip zincirin en güçlü halkası kadar sağlamdır.' },
  { icon: '🤜', text: 'Birlikte çalışmak, tek başına başarmanın ötesine geçmektir.' },
  { icon: '🎙️', text: 'Fikirlerini paylaş; en iyi çözümler diyalogdan doğar.' },
  { icon: '🏗️', text: 'Güçlü temeller, güçlü ekiplerle atılır.' },
  { icon: '🔗', text: 'Departmanlar arası uyum, müşteriye kesintisiz deneyim sunar.' },
  { icon: '🫱🏼‍🫲🏽', text: 'Karşılıklı saygı, verimli iş birliğinin temelidir.' },
  { icon: '📣', text: 'Açık iletişim, yanlış anlamaların en büyük düşmanıdır.' },
  { icon: '🎯', text: 'Ortak hedef belirlemek, bireysel motivasyonu da artırır.' },
  { icon: '🧲', text: 'Pozitif enerji bulaşıcıdır; ekibine ilham ver.' },
  // ── Verimlilik & Zaman ──
  { icon: '⏱️', text: 'Zamanı yönetmek, stresi yönetmek demektir.' },
  { icon: '📋', text: 'Günü planla, sonra planı uygula. Sıra önemli.' },
  { icon: '🗂️', text: 'Düzenli arşiv, kriz anında en iyi dostundur.' },
  { icon: '⚡', text: 'Hız önemli ama doğruluk daha önemli.' },
  { icon: '🔄', text: 'Tekrar eden işleri otomatikleştir, yaratıcılığa zaman aç.' },
  { icon: '📅', text: 'Takvimini kontrol et, takvimin seni kontrol etmesin.' },
  { icon: '🧹', text: 'Masanı temiz tut, zihnini daha temiz tut.' },
  { icon: '🎯', text: 'Önceliklendirme, her şeyi yapmak değil doğru şeyi yapmaktır.' },
  { icon: '⏳', text: 'Bugün ertelenen iş, yarın iki katına çıkar.' },
  { icon: '🛠️', text: 'Doğru araç, doğru iş: AgentHUB tam da bunun için var.' },
  // ── Gelişim & Öğrenme ──
  { icon: '📚', text: 'Her gün yeni bir şey öğrenmek, rekabet avantajıdır.' },
  { icon: '🔬', text: 'Merak etmek, gelişimin ilk adımıdır.' },
  { icon: '🎓', text: 'Bilgi paylaştıkça çoğalır; deneyimini ekibinle paylaş.' },
  { icon: '🧠', text: 'Hatalardan ders almak, onları tekrarlamamanın garantisidir.' },
  { icon: '📖', text: 'Sektör trendlerini takip et, müşterine bir adım önde ol.' },
  { icon: '🌐', text: 'Yeni bir dil öğrenmek, yeni bir pazarın kapısını açar.' },
  { icon: '💻', text: 'Teknolojiyi takip eden, sektörü de takip eder.' },
  { icon: '🪴', text: 'Sabır ve süreklilik, uzmanlığın iki sütunudur.' },
  { icon: '🔭', text: 'Büyük düşün, küçük başla, hızlı öğren.' },
  { icon: '🏋️', text: 'Konfor alanından çıkmak, büyümenin başladığı yerdir.' },
  // ── Motivasyon & Tutum ──
  { icon: '☀️', text: 'Pozitif başlayan gün, pozitif sonuçlar getirir.' },
  { icon: '🔥', text: 'Tutku, yorgunluğun en iyi ilacıdır.' },
  { icon: '🧗', text: 'Zirveye kolay çıkılmaz ama manzara buna değer.' },
  { icon: '🎶', text: 'İşini severek yapmak, en verimli çalışma yöntemidir.' },
  { icon: '🦅', text: 'Perspektifini genişlet; detayı kaybetmeden büyük resmi gör.' },
  { icon: '🌈', text: 'Zor günlerin ardından en güzel başarılar gelir.' },
  { icon: '💫', text: 'Kendine güven; doğru bilgiyle donatılmış bir profesyonel her engeli aşar.' },
  { icon: '🍀', text: 'Şans, hazırlıklı olanın kapısını çalar.' },
  { icon: '🎆', text: 'Bugünün gayreti, yarının gururu olacak.' },
  { icon: '🌊', text: 'Dalgalarla savaşma, sörf yapmayı öğren.' },
  // ── Finansal Farkındalık ──
  { icon: '💰', text: 'Nakit akışı, şirketin nabız atışıdır; gözünü üzerinden ayırma.' },
  { icon: '🧮', text: 'Rakamlar yalan söylemez; raporlarını düzenli incele.' },
  { icon: '📉', text: 'Maliyeti kontrol etmek, kârı artırmanın en hızlı yoludur.' },
  { icon: '🏦', text: 'Vade takibi, finansal sağlığın koruyucu kalkanıdır.' },
  { icon: '💳', text: 'Her harcama kaydı, gelecekteki doğru kararın temelidir.' },
  { icon: '📊', text: 'Marj analizi, hangi hizmetin gerçekten kazandırdığını gösterir.' },
  { icon: '🪙', text: 'Küçük tasarruflar, yıl sonunda büyük farklar yaratır.' },
  { icon: '🤑', text: 'Tahsilat takibi, satış kadar önemlidir.' },
  { icon: '📑', text: 'Fatura düzeni, profesyonelliğin finansal yansımasıdır.' },
  { icon: '🔐', text: 'Finansal veri güvenliği, müşteri güveninin temelidir.' },
  // ── Liderlik & Vizyon ──
  { icon: '🗼', text: 'Vizyon sahibi ol; ekibin nereye gittiğini bilmeli.' },
  { icon: '🧭', text: 'Liderlik, yol göstermek kadar yanında yürümektir.' },
  { icon: '🪜', text: 'Başkalarını yukarı taşıyan, kendisi de yükselir.' },
  { icon: '🏹', text: 'Strateji olmadan taktik, plansız koşmaktır.' },
  { icon: '🌤️', text: 'Kriz anında sakin kalmak, liderliğin gerçek sınavıdır.' },
  { icon: '📌', text: 'Net hedefler koy; belirsizlik motivasyonun düşmanıdır.' },
  { icon: '🎖️', text: 'Takdir etmek maliyetsizdir ama etkisi paha biçilemez.' },
  { icon: '🧱', text: 'Kurum kültürü, yazılı kurallardan çok günlük davranışlarla inşa edilir.' },
  { icon: '🌟', text: 'İyi bir lider, "ben" değil "biz" der.' },
  { icon: '🧿', text: 'Güven inşa etmek zaman alır, korumak ise her günkü işindir.' },
];

const NOTIF_ICONS = {
  user_plus: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg>,
  file_import: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><polyline points="9 15 12 12 15 15"/></svg>,
  building: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2" ry="2"/><path d="M9 22v-4h6v4"/><line x1="8" y1="6" x2="10" y2="6"/><line x1="14" y1="6" x2="16" y2="6"/><line x1="8" y1="10" x2="10" y2="10"/><line x1="14" y1="10" x2="16" y2="10"/><line x1="8" y1="14" x2="10" y2="14"/><line x1="14" y1="14" x2="16" y2="14"/></svg>,
  alert_warning: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
  alert_danger: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
};

const TopBar = () => {
  const { user, logout, hasPermission, isAdmin } = useAuth();
  const { masked, toggleMask } = useMask();
  const { theme, toggle: toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [tasksOpen, setTasksOpen] = useState(false);
  // V71.14 — görevler (sistem + atanan) ve kalıcı bildirimler ayrı
  const [sysTasks, setSysTasks] = useState([]);
  const [myTasks, setMyTasks] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [profilePct, setProfilePct] = useState(100);
  const ref = useRef(null);
  const notifRef = useRef(null);
  const tasksRef = useRef(null);
  const otpSocketRef = useRef(null);
  const audioCtxRef = useRef(null);
  const otpCfgRef = useRef({ sound: 'on', tone: 'ding' });
  const [otpToasts, setOtpToasts] = useState([]); // [{id, row, countdown}]
  const otpTickRef = useRef(null);

  /* ── Global OTP bildirim sesi ── */
  const playOtpSound = useCallback(() => {
    const cfg = otpCfgRef.current;
    if (cfg.sound === 'off') return;
    try {
      if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended') ctx.resume();
      const now = ctx.currentTime;
      const play = (freq, type, start, dur, vol) => {
        const osc = ctx.createOscillator(); const g = ctx.createGain();
        osc.type = type; osc.frequency.value = freq;
        g.gain.setValueAtTime(vol, now + start);
        g.gain.exponentialRampToValueAtTime(0.001, now + start + dur);
        osc.connect(g).connect(ctx.destination);
        osc.start(now + start); osc.stop(now + start + dur + 0.05);
      };
      const t = cfg.tone || 'ding';
      if (t === 'ding') { play(880,'sine',0,0.35,0.18); play(1174.66,'sine',0.12,0.35,0.18); }
      else if (t === 'chime') { play(523.25,'sine',0,0.3,0.15); play(659.25,'sine',0.1,0.3,0.15); play(783.99,'sine',0.2,0.4,0.18); }
      else if (t === 'alert') { play(1200,'square',0,0.08,0.12); play(1200,'square',0.12,0.08,0.12); play(1600,'square',0.24,0.12,0.14); }
      else { play(440,'sine',0,0.5,0.12); play(554.37,'sine',0.05,0.5,0.10); play(659.25,'sine',0.1,0.6,0.08); }
    } catch {}
  }, []);

  // V71 — OTP kodları yalnız yetkiliye: backend 'otp_feed' odasına yalnız admin / otp_feed / otp_feed_widget
  // görüntüleme yetkilisini alır; arayüz de aynı yetkiyle soket açar ve toast gösterir
  const canOtp = isAdmin || hasPermission('otp_feed', 'view') || hasPermission('otp_feed_widget', 'view');
  const uid = user?.id;
  // Yeniden girişte (completeLogin) token değişir → soket taze token'la yeniden kurulur; /me yenilemesi token'ı değiştirmez
  let otpTok = null;
  try { otpTok = user ? localStorage.getItem('mtu_token') : null; } catch {}

  useEffect(() => {
    if (!canOtp) return;
    otpFeedAPI.getTone().then(r => {
      otpCfgRef.current = { sound: r.data.sound || 'on', tone: r.data.tone || 'ding' };
    }).catch(() => {});
  }, [canOtp]);

  useEffect(() => {
    const token = otpTok;
    if (!token || !uid || !canOtp) { setOtpToasts([]); return; }
    const SOCKET_URL = (process.env.REACT_APP_API_URL || window.location.origin).replace(/\/api$/, '');
    otpSocketRef.current = io(SOCKET_URL, {
      // Her bağlanma/yeniden bağlanma el sıkışması güncel token'ı okur
      auth: (cb) => { let t = token; try { t = localStorage.getItem('mtu_token') || token; } catch {} cb({ token: t }); },
      transports: ['polling'],
      upgrade: false, // Railway WS upgrade fail oluyor — polling yeterli
      reconnectionAttempts: 10,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 30000,
    });
    otpSocketRef.current.on('otp:new', (row) => {
      playOtpSound();
      if (row) {
        const baseTime = 20;
        setOtpToasts(prev => {
          // max 5 toast, en eski düşer
          const next = [...prev, { id: Date.now(), row, countdown: baseTime }].slice(-5);
          // Birden fazla toast varken hepsinin süresini uzat
          if (next.length > 1) {
            return next.map(t => ({ ...t, countdown: Math.max(t.countdown, baseTime) }));
          }
          return next;
        });
      }
    });
    return () => { otpSocketRef.current?.disconnect(); };
    // V71 — yalnız kullanıcı/yetki/token değişince yeniden bağlan (user nesnesi her /me'de yenileniyordu)
  }, [uid, canOtp, otpTok, playOtpSound]);

  /* Toast countdown tick — her saniye */
  const hasToasts = otpToasts.length > 0;
  useEffect(() => {
    if (!hasToasts) { clearInterval(otpTickRef.current); return; }
    otpTickRef.current = setInterval(() => {
      setOtpToasts(prev => prev.map(t => ({ ...t, countdown: t.countdown - 1 })).filter(t => t.countdown > 0));
    }, 1000);
    return () => clearInterval(otpTickRef.current);
  }, [hasToasts]);

  /* Esc → hepsini kapat, Enter → en üsttekini kapat */
  useEffect(() => {
    if (!hasToasts) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setOtpToasts([]);
      else if (e.key === 'Enter') setOtpToasts(prev => prev.slice(1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hasToasts]);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
      if (tasksRef.current && !tasksRef.current.contains(e.target)) setTasksOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const fetchTasks = () => {
    taskAPI.summary()
      .then(res => { setSysTasks(res.data.system || []); setMyTasks(res.data.assigned || []); })
      .catch(() => {});
  };

  const fetchNotifs = () => {
    notificationAPI.list({ limit: 8 })
      .then(res => { setNotifs(res.data.data || []); setUnread(res.data.unread || 0); })
      .catch(() => {});
  };

  useEffect(() => {
    fetchTasks();
    fetchNotifs();
    authAPI.getProfile().then(r => { if (r.data?.profile_completion) setProfilePct(r.data.profile_completion.pct); }).catch(() => {});
    const interval = setInterval(() => { fetchTasks(); fetchNotifs(); }, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const openNotif = (n) => {
    setNotifOpen(false);
    if (!n.read_at) notificationAPI.markRead(n.id).then(fetchNotifs).catch(() => {});
    if (n.link) navigate(n.link);
  };

  const markAllRead = () => {
    notificationAPI.markAllRead().then(fetchNotifs).catch(() => {});
  };

  const initials = user?.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '?';

  // Daily motivation (seed by date)
  const today = new Date();
  const seed = today.getFullYear() * 10000 + (today.getMonth() + 1) * 100 + today.getDate();
  const motiv = MOTIVATION_MESSAGES[seed % MOTIVATION_MESSAGES.length];
  const firstName = user?.full_name ? user.full_name.split(' ')[0] : '';

  const taskCount = sysTasks.length + myTasks.length;
  const bellCount = unread;

  // V71 — zil animasyonu sürekli değil: yalnız okunmamış sayı ARTTIĞINDA bir kez oynar (key ile yeniden tetiklenir)
  const [bellAnim, setBellAnim] = useState(0);
  const prevBellRef = useRef(0);
  // V71.14.3 — birleşik üst satırda (data-tb-merged) şerit sağdan üst çubuk genişliği kadar boşluk bırakır
  const barRef = useRef(null);
  useEffect(() => {
    const el = barRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => document.documentElement.style.setProperty('--tb-w', `${el.offsetWidth}px`));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  useEffect(() => {
    if (bellCount > prevBellRef.current) setBellAnim(n => n + 1);
    prevBellRef.current = bellCount;
  }, [bellCount]);

  const icoTheme = theme === 'dark'
    ? <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
    : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>;
  const icoMask = masked
    ? <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
    : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>;
  const icoLayout = <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>;
  const icoSettings = <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9c.26.604.852.997 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>;

  return (
    <div className="top-bar" ref={barRef}>
      <button className="top-bar-hamburger" onClick={() => window.__sidebarMobileToggle && window.__sidebarMobileToggle()} title="Menü" aria-label="Menü">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
      </button>
      <div className="top-bar-greeting">
        <span className="top-bar-hello">{firstName ? `Hoş geldin, ${firstName}` : 'Hoş geldin'} {motiv.icon}</span>
        <span className="top-bar-motiv">{motiv.text}</span>
      </div>

      <WorkTypeSwitch />
      {/* V71.28 — Takvim menüden üst çubuğa taşındı */}
      <button className="top-bar-settings-btn" onClick={() => navigate('/calendar')} title="Takvim" aria-label="Takvim">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
      </button>

      {/* ── Görevlerim — V71.14: sistem + bana atanan görevler, tamamı /tasks ── */}
      <div className="top-bar-notif-wrap" ref={tasksRef}>
        <button className={`top-bar-tasks-btn${taskCount > 0 ? ' has-tasks' : ''}`} onClick={() => setTasksOpen(!tasksOpen)} title="Görevlerim" aria-label="Görevlerim">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
          {taskCount > 0 && <span className="top-bar-badge top-bar-badge-tasks">{taskCount}</span>}
        </button>
        {tasksOpen && (
          <div className="top-bar-notif-dropdown top-bar-tasks-dropdown">
            <div className="top-bar-notif-header">Görevlerim</div>
            {taskCount === 0 ? (
              <div className="top-bar-notif-empty">Açık görev yok</div>
            ) : (
              <>
                {myTasks.slice(0, 6).map(t => (
                  <button key={`t${t.id}`} className="top-bar-notif-item top-bar-task-item" onClick={() => { setTasksOpen(false); navigate(`/tasks?id=${t.id}`); }}>
                    <span className="top-bar-task-status pending">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/></svg>
                    </span>
                    <span className="top-bar-notif-text">{t.title}</span>
                    {t.due_date && <span className={`top-bar-task-label${t.due_date < new Date().toISOString().slice(0, 10) ? ' rejected' : ' waiting'}`}>{t.due_date.split('-').reverse().join('.')}</span>}
                  </button>
                ))}
                {sysTasks.length > 0 && myTasks.length > 0 && <div className="top-bar-tasks-divider">Sistem</div>}
                {sysTasks.map(n => (
                  <button key={n.id} className="top-bar-notif-item top-bar-task-item" onClick={() => { setTasksOpen(false); navigate(n.link); }}>
                    <span className="top-bar-notif-icon">{NOTIF_ICONS[n.icon] || '🔔'}</span>
                    <span className="top-bar-notif-text">{n.title}</span>
                    {n.count > 1 && <span className="top-bar-notif-count">{n.count}</span>}
                  </button>
                ))}
              </>
            )}
            <button className="top-bar-notif-footer" onClick={() => { setTasksOpen(false); navigate('/tasks'); }}>Tüm görevler →</button>
          </div>
        )}
      </div>

      {/* ── Bildirimler — V71.14: kalıcı, okundu/okunmadı; tamamı /notifications ── */}
      <div className="top-bar-notif-wrap" ref={notifRef}>
        <button key={`bell-${bellAnim}`} className={`top-bar-bell${bellCount > 0 ? ' active' : ''}`} onClick={() => { setNotifOpen(!notifOpen); }} title="Bildirimler" aria-label="Bildirimler">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
          {bellCount > 0 && <span className="top-bar-badge">{bellCount > 99 ? '99+' : bellCount}</span>}
        </button>
        {notifOpen && (
          <div className="top-bar-notif-dropdown">
            <div className="top-bar-notif-header">
              Bildirimler
              {unread > 0 && <button className="top-bar-notif-readall" onClick={markAllRead}>Tümünü okundu yap</button>}
            </div>
            {notifs.length === 0 ? (
              <div className="top-bar-notif-empty">Bildirim yok</div>
            ) : (
              notifs.map(n => (
                <button key={n.id} className={`top-bar-notif-item${n.read_at ? ' read' : ''}`} onClick={() => openNotif(n)}>
                  <span className="top-bar-notif-icon">{n.type === 'task' ? '📋' : n.type === 'system_task' ? '⚠️' : n.type === 'hr' ? '🌴' : n.type === 'hata' ? '🐞' : n.type === 'fuar' ? '🗓️' : n.type === 'santral' ? '📞' : '🔔'}</span>
                  <span className="top-bar-notif-text">
                    {n.title}
                    {n.body && <small className="top-bar-notif-sub">{n.body}</small>}
                  </span>
                  {!n.read_at && <span className="top-bar-notif-dot" />}
                </button>
              ))
            )}
            <button className="top-bar-notif-footer" onClick={() => { setNotifOpen(false); navigate('/notifications'); }}>Tüm bildirimler →</button>
          </div>
        )}
      </div>
      {/* V71 — Döviz Çevirici düğmesi kaldırıldı: tek giriş Hızlı Araçlar → Kur Çevirici (aynı modal) */}
      {/* V71.28 — Tema / Veri Gizleme / Dashboard Ayarları / Ayarlar üst çubuktan avatar menüsüne taşındı */}

      <div className="top-bar-right" ref={ref}>
        <button className="top-bar-user" onClick={() => setOpen(!open)} aria-haspopup="menu" aria-expanded={open}>
          <div className="top-bar-avatar-wrap">
            <div className="top-bar-avatar">{initials}</div>
            {profilePct < 100 && <span className="top-bar-avatar-pct">%{profilePct}</span>}
          </div>
          <div className="top-bar-info">
            <span className="top-bar-name">{user?.full_name}</span>
            <span className="top-bar-role">{isAdmin ? 'Admin' : 'Kullanıcı'}</span>
          </div>
          <svg className={`top-bar-chevron ${open ? 'open' : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>
        </button>

        {open && (
          <div className="top-bar-dropdown" role="menu">
            <button className="top-bar-dropdown-item" onClick={() => { navigate('/profile'); setOpen(false); }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              Profil Ayarları
              {profilePct < 100 && <span className="top-bar-dropdown-pct">%{profilePct}</span>}
            </button>
            <button className="top-bar-dropdown-item" onClick={() => { navigate('/security'); setOpen(false); }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              Güvenlik Ayarları
            </button>
            <div className="top-bar-dropdown-divider" />
            <button className="top-bar-dropdown-item" onClick={() => { toggleTheme(); setOpen(false); }}>
              {icoTheme}{theme === 'dark' ? 'Açık Tema' : 'Koyu Tema'}
            </button>
            <button className="top-bar-dropdown-item" onClick={() => { toggleMask(); setOpen(false); }}>
              {icoMask}{masked ? 'Verileri Göster' : 'Verileri Gizle'}
            </button>
            <button className="top-bar-dropdown-item" onClick={() => { navigate('/my-dashboard'); setOpen(false); }}>
              {icoLayout}Dashboard Ayarları
            </button>
            {isAdmin && (
              <button className="top-bar-dropdown-item" onClick={() => { navigate('/settings'); setOpen(false); }}>
                {icoSettings}Ayarlar
              </button>
            )}
            <div className="top-bar-dropdown-divider" />
            <button className="top-bar-dropdown-item danger" onClick={handleLogout}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
              Çıkış Yap
            </button>
          </div>
        )}
      </div>

      {/* OTP Toast Stack — V71: body'ye portal + sınıflar (dark uyumlu) */}
      {otpToasts.length > 0 && createPortal(
        <div className="otp-toast-stack">
          {otpToasts.map((t) => {
            const r = t.row;
            const bc = r.parsed_bank === 'THY' ? '#e11d48' : r.parsed_bank === 'GARANTI' ? '#16a34a' : r.parsed_bank === 'ISBANK' ? '#0ea5e9' : r.parsed_bank === 'AKBANK' ? '#dc2626' : '#6366f1';
            return (
              <div
                key={t.id}
                className="otp-toast"
                style={{ '--otp-bc': bc }}
                onClick={() => setOtpToasts(prev => prev.filter(x => x.id !== t.id))}
              >
                <div className="otp-toast-inner">
                  <div className="otp-toast-top">
                    <span className="otp-toast-bank">{r.parsed_bank || 'OTP'}</span>
                    <span className="otp-toast-sender">{r.sender || ''}</span>
                    <span className="otp-toast-count">{t.countdown}s</span>
                  </div>
                  <div className="otp-toast-row">
                    <div className="otp-toast-code">{r.parsed_code || '—'}</div>
                    <button
                      className="btn btn-secondary btn-xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        try { navigator.clipboard.writeText(r.parsed_code || ''); } catch {}
                      }}
                    >
                      Kopyala
                    </button>
                  </div>
                  {r.body && <div className="otp-toast-body">{r.body}</div>}
                  {otpToasts.length > 1 && (
                    <div className="otp-toast-hint">tıkla / ESC hepsini kapat · Enter tek kapat</div>
                  )}
                </div>
                <div className="otp-toast-bar">
                  <div style={{ width: `${(t.countdown / 20) * 100}%` }} />
                </div>
              </div>
            );
          })}
        </div>,
        document.body
      )}

      {/* V70.148 — Hızlı Araçlar: kenara yapışan sürüklenebilir radial araç menüsü (portal → body) */}
      <FloatingTools />
      <SantralCagriKarti />
    </div>
  );
};

export default TopBar;
